/**
 * Derive scan activity for CRM contacts from Firestore `scans` (no GA4 / user-profile fields required).
 */
import { productDocUserRef } from "./productAnalyticsMetrics";
import { normalizeLifecycleStage, stageRank, STAGE_RANK } from "./lifecycleStages";

function tsMs(v) {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

export function scanDocTimestampMs(scan) {
  return tsMs(
    scan?.captured_at ??
      scan?.capturedAt ??
      scan?.createdAt ??
      scan?.created_at ??
      scan?.timestamp ??
      scan?.uploadedAt ??
      scan?.uploaded_at
  );
}

export function contactUserKeysForRollup(contact) {
  const set = new Set();
  const id = contact?.id != null ? String(contact.id).trim() : "";
  const uid = contact?.firebaseUid != null ? String(contact.firebaseUid).trim() : "";
  const email = contact?.email != null ? String(contact.email).trim().toLowerCase() : "";
  if (id) set.add(id);
  if (uid) set.add(uid);
  if (email && email.includes("@")) set.add(email);
  return set;
}

/** @returns {Map<string, { count: number, lastScanMs: number|null }>} */
export function buildScanRollupByUserRef(scans = []) {
  const map = new Map();
  for (const scan of scans) {
    const u = productDocUserRef(scan);
    if (!u) continue;
    const ms = scanDocTimestampMs(scan);
    const prev = map.get(u);
    if (!prev) {
      map.set(u, { count: 1, lastScanMs: ms != null && ms > 0 ? ms : null });
      continue;
    }
    prev.count += 1;
    if (ms != null && ms > 0 && (prev.lastScanMs == null || ms > prev.lastScanMs)) {
      prev.lastScanMs = ms;
    }
  }
  return map;
}

function lookupRollupForContact(contact, rollup) {
  let count = 0;
  let lastScanMs = null;
  for (const k of contactUserKeysForRollup(contact)) {
    const row = rollup.get(k);
    if (!row) continue;
    count = Math.max(count, row.count);
    if (row.lastScanMs != null && (lastScanMs == null || row.lastScanMs > lastScanMs)) {
      lastScanMs = row.lastScanMs;
    }
  }
  return { count, lastScanMs };
}

function mergeRollupLookups(...lookups) {
  let count = 0;
  let lastScanMs = null;
  for (const row of lookups) {
    if (!row) continue;
    count = Math.max(count, row.count || 0);
    if (row.lastScanMs != null && (lastScanMs == null || row.lastScanMs > lastScanMs)) {
      lastScanMs = row.lastScanMs;
    }
  }
  return { count, lastScanMs };
}

/** Merge Firestore scan docs into contact scan fields for list cards and filters. */
export function enrichContactWithScanRollup(contact, rollup, fileRollup = null) {
  if (!contact) return contact;
  const scanLookup = lookupRollupForContact(contact, rollup);
  const fileLookup = fileRollup ? lookupRollupForContact(contact, fileRollup) : { count: 0, lastScanMs: null };
  const { count, lastScanMs } = mergeRollupLookups(scanLookup, fileLookup);
  const profileTotal = Number(contact.totalScans) || 0;
  const totalScans = Math.max(profileTotal, count);
  const hasScannedFromDocs = count > 0;
  const stage = normalizeLifecycleStage(contact.stage);
  const hasScannedFromStage = stageRank(stage) >= STAGE_RANK.has_scanned;
  const hasScanned = hasScannedFromDocs || hasScannedFromStage;
  const displayStage =
    hasScannedFromDocs && stageRank(stage) < STAGE_RANK.has_scanned ? "has_scanned" : stage;

  let lastScanAt = contact.lastScanAt;
  if (lastScanMs != null && lastScanMs > 0) {
    const existingMs = tsMs(lastScanAt);
    if (existingMs == null || lastScanMs > existingMs) {
      lastScanAt = new Date(lastScanMs);
    }
  }

  return {
    ...contact,
    totalScans,
    lastScanAt,
    scanDocCount: count,
    hasScannedFromDocs,
    hasScanned,
    displayStage,
  };
}

export function enrichContactsWithScanRollup(contacts = [], scans = [], files = []) {
  const rollup = buildScanRollupByUserRef(scans);
  const fileRollup = files.length ? buildScanRollupByUserRef(files) : null;
  return contacts.map((c) => enrichContactWithScanRollup(c, rollup, fileRollup));
}

/** True when contact has scan docs and/or lifecycle stage at/after “has scanned”. */
export function contactHasScanned(contact) {
  if (!contact) return false;
  if (contact.hasScanned === true) return true;
  if ((Number(contact.scanDocCount) || 0) > 0) return true;
  const stage = normalizeLifecycleStage(contact.stage);
  return stageRank(stage) >= STAGE_RANK.has_scanned;
}
